import React, { useState, useEffect } from "react";
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useAddInternalUserStyles from './styles';
import {
  AddInternalUserNew,
  UpdateUserNew,
  FetchRoles,
  FetchOrganizations,
  FetchDepartments
} from "../../../../services/api";

const AddInternalUsers = ({ handleTabChange, logic, isModal, onClose }) => {
  const classes = useAddInternalUserStyles();
  const [isEditMode, setIsEditMode] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [orgList, setOrgList] = useState([]);
  // const [deptList, setDeptList] = useState([]); // Not strictly used in dropdown if hardcoded, but kept for consistency

  const token = localStorage.getItem('accessToken');

  // Validation Schema
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .trim()
      .min(2, 'First Name must be at least 2 characters')
      .max(50, 'First Name must not exceed 50 characters')
      .required('First Name is required'),
    lastName: Yup.string()
      .trim()
      .min(2, 'Last Name must be at least 2 characters')
      .max(50, 'Last Name must not exceed 50 characters')
      .required('Last Name is required'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email ID is required'),
    phone: Yup.string()
      .matches(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format')
      .required('Contact Number is required'),
    department: Yup.string().required('Department is required'),
    // hierarchy: Yup.string().required('Hierarchy is required'),
    organizationId: Yup.string().required('Organization is required'),
    roleId: Yup.string().required('Role is required'),
    password: Yup.string()
      .when('isEditMode', {
        is: false, // Add mode
        then: () => Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 special char')
          .required('Password is required'),
        otherwise: () => Yup.string()
          .min(8, 'Password must be at least 8 characters')
          .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 special char')
          .nullable().optional() // Optional in edit mode
      }),
    confirmPassword: Yup.string()
      .when('password', {
        is: (val) => val && val.length > 0,
        then: () => Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('Confirm Password is required'),
        otherwise: () => Yup.string().nullable().optional()
      })
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      department: '',
      // hierarchy: '',
      organizationId: '',
      roleId: '',
      password: '',
      confirmPassword: '',
      isEditMode: false, // Helper for Yup
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setErrors }) => {
      setSubmitting(true);

      const payload = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        userType: "internal",
        status: "active",
        organizationId: values.organizationId,
        roleIds: [values.roleId],
        metadata: {
          department: values.department,
          // hierarchy: values.hierarchy,
        }
      };

      if (values.password) {
        payload.password = values.password;
      }

      try {
        let resp;
        if (isEditMode && logic?.editData) {
          const userId = logic.editData.id || logic.editData._id || logic.editData.userId;
          resp = await UpdateUserNew(userId, payload, token);
        } else {
          resp = await AddInternalUserNew(payload, token);
        }

        if (resp?.data) {
          if (isEditMode) {
            logic.setters.setEditData(null);
          }
          if (isModal && onClose) {
            onClose();
          } else {
            handleTabChange('1', '1');
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        // Capture backend errors if structured
        if (error.response?.data?.message) {
          // If generic error, maybe alert? For now console or minimal mapping
          // Ideally map field errors if backend returns standard validation error object
          if (error.response.data.message.includes('email')) {
            setFieldError('email', error.response.data.message);
          } else {
            // Fallback alert or general error logic can go here
            alert(error.response.data.message);
          }
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Fetch Master Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const rolesResp = await FetchRoles(token);
        if (rolesResp?.data?.roles) setRolesList(rolesResp.data.roles);

        const orgResp = await FetchOrganizations(token);
        if (orgResp?.data?.data) setOrgList(orgResp.data.data);

        // const deptResp = await FetchDepartments(token);
        // if (deptResp?.data?.data) setDeptList(deptResp.data.data);
      } catch (err) {
        console.error("Failed to fetch master data", err);
      }
    };
    fetchData();
  }, [token]);

  // Pre-fill data if in Edit Mode
  useEffect(() => {
    if (logic?.editData) {
      const data = logic.editData;
      setIsEditMode(true);
      formik.setValues({
        firstName: data.firstName || data.name?.split(' ')[0] || "",
        lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || "",
        email: data.email || "",
        phone: data.phone || "",
        department: data.metadata?.department || "",
        // hierarchy: data.metadata?.hierarchy || "",
        roleId: data.role?.id || data.roles?.[0]?.id || data.metadata?.roleId || "",
        organizationId: data.organization?.id || data.organizationId || data.metadata?.organizationId || "",
        password: '',
        confirmPassword: '',
        isEditMode: true
      });
    } else {
      setIsEditMode(false);
      formik.setFieldValue('isEditMode', false);
    }
  }, [logic?.editData]);

  return (
    <div className={classes.root}>
      <form onSubmit={formik.handleSubmit}>
        <div className="graph_bg">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="firstName"
                  name="firstName"
                  label="First Name*"
                  variant="outlined"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="lastName"
                  name="lastName"
                  label="Last Name*"
                  variant="outlined"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="phone"
                  name="phone"
                  label="Contact No.*"
                  variant="outlined"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.department && Boolean(formik.errors.department)}>
                <InputLabel id="user-dept-label">Department</InputLabel>
                <Select
                  labelId="user-dept-label"
                  id="department"
                  name="department"
                  value={formik.values.department}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Department"
                >
                  <MenuItem value="Technology">Technology</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Investment">Investment</MenuItem>
                  <MenuItem value="Procurement & Logistics">Procurement & Logistics</MenuItem>
                  <MenuItem value="Asset Management">Asset Management</MenuItem>
                  <MenuItem value="Design">Design</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Pre Sales">Pre Sales</MenuItem>
                  <MenuItem value="Projects">Projects</MenuItem>
                  <MenuItem value="Regulatory Affairs">Regulatory Affairs</MenuItem>
                  <MenuItem value="Site Engineer">Site Engineer</MenuItem>

                </Select>
                <FormHelperText>{formik.touched.department && formik.errors.department}</FormHelperText>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.hierarchy && Boolean(formik.errors.hierarchy)}>
                <InputLabel id="user-hierarchy-label">Hierarchy</InputLabel>
                <Select
                  labelId="user-hierarchy-label"
                  id="hierarchy"
                  name="hierarchy"
                  value={formik.values.hierarchy}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Hierarchy"
                >
                  <MenuItem value="Director">Director</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                  <MenuItem value="Employee">Employee</MenuItem>
                </Select>
                <FormHelperText>{formik.touched.hierarchy && formik.errors.hierarchy}</FormHelperText>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.organizationId && Boolean(formik.errors.organizationId)}>
                <InputLabel id="user-org-label">Organization</InputLabel>
                <Select
                  labelId="user-org-label"
                  id="organizationId"
                  name="organizationId"
                  value={formik.values.organizationId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Organization"
                >
                  {orgList.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.organizationName}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formik.touched.organizationId && formik.errors.organizationId}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={formik.touched.roleId && Boolean(formik.errors.roleId)}>
                <InputLabel id="user-role-label">Role</InputLabel>
                <Select
                  labelId="user-role-label"
                  id="roleId"
                  name="roleId"
                  value={formik.values.roleId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Role"
                >
                  {rolesList.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formik.touched.roleId && formik.errors.roleId}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="email"
                  name="email"
                  label="Email ID*"
                  variant="outlined"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </FormControl>
            </Grid>
            {(!isEditMode || formik.values.password) && (
              // Show password fields if not in edit mode OR if user has started typing a password in edit mode 
              // Logic refinement: In edit mode, hidden unless explicitly requested or handled. 
              // Requirement says: "Password is required (add mode), nullable (edit mode)"
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      id="password"
                      name="password"
                      label={isEditMode ? "New Password (Optional)" : "Password*"}
                      variant="outlined"
                      type="password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      id="confirmPassword"
                      name="confirmPassword"
                      label="Confirm Password"
                      variant="outlined"
                      type="password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                      helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
          <button
            type="submit"
            className={`save_btn ${formik.isValid && (formik.dirty || isEditMode) ? "" : "disabled"}`}
            disabled={formik.isSubmitting || !(formik.isValid && (formik.dirty || isEditMode))}
          >
            {formik.isSubmitting ? "Saving..." : (isEditMode ? "Update" : "Save")}
          </button>
        </div>
      </form>
    </div>
  );
};

AddInternalUsers.propTypes = {};

export default AddInternalUsers;
